/**
 * Host classification — the one place that says what kind of host this is.
 *
 * Several unrelated decisions hinge on it and must never disagree:
 *  - isAmdHost: which cookie domain the session mirror writes
 *    (security/auth/cookies.js), so the session is shared across *.amd.com.
 *  - isLocalHost: whether URLs carry the /cca | /eia prefix (router/basename.js)
 *    AND whether the local email + password form may exist at all
 *    (config/loginMode.js). One helper for both means the dev-only URL scheme
 *    and the dev-only sign-in can never disagree about what "local" is.
 *
 * Pure, and takes the hostname rather than reading `window`, so every rule is
 * directly unit-testable.
 */

/**
 * True for `amd.com` itself and any subdomain of it.
 *
 * Matches on a full label: `cca.amd.com` and `dev.epycadvisory.amd.com` are AMD,
 * but `notamd.com` and `amd.com.evil.io` are not. A bare `endsWith('amd.com')`
 * would accept the first, and anything that merely CONTAINS 'amd.com' would
 * accept the second — which would send a look-alike host the SSO flow and the
 * shared session cookie.
 *
 * @param {string | undefined | null} hostname
 * @returns {boolean}
 */
export function isAmdHost(hostname) {
  const host = String(hostname ?? '').toLowerCase();
  return host === 'amd.com' || host.endsWith('.amd.com');
}

/**
 * True when the app is being served from this machine — local development.
 *
 * `localhost` and its subdomains (browsers resolve `*.localhost` to loopback),
 * the IPv4 loopback block 127.0.0.0/8, and IPv6 loopback. Deliberately NOT a
 * LAN address such as 192.168.x.x: that is someone else's machine reaching a
 * dev server, and is treated like any other non-local host.
 *
 * @param {string | undefined | null} hostname
 * @returns {boolean}
 */
export function isLocalHost(hostname) {
  const host = String(hostname ?? '').toLowerCase();
  return (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    /^127(\.\d{1,3}){3}$/.test(host) ||
    host === '::1' ||
    host === '[::1]'
  );
}
